/**
 * Background texture feature
 * Dynamically changes background texture based on scroll position and active chapter
 * Each chapter-title scene can define "background" (filename in /assets/images/)
 */

import type { ChapterTitleScene, BackgroundTextureInstance } from './types'
import { resolveTextureUrl } from '../../utils/pathResolver'

const FALLBACK_TEXTURE = 'texture-dark.jpg'

/**
 * Setup background texture change based on active chapter
 * @param scenes - Array of scene data from story.json (chapter-title scenes may have "background")
 * @param frameCallbacks - Array to push update callbacks for frame-by-frame updates
 * @returns Background texture instance with destroy and update methods
 */
export function setupBackgroundTexture(
  scenes: ChapterTitleScene[],
  frameCallbacks: (() => void)[],
): BackgroundTextureInstance {
  // Story-order list of chapter-title scene ids (index = chapter index in story)
  const chapterSceneIds = scenes.filter((s) => s.type === 'chapter-title').map((s) => s.id)
  const chapterScenes = scenes
    .map((scene) =>
      scene.type === 'chapter-title'
        ? {
            texturePath: resolveTextureUrl(
              scene.background && scene.background.trim() ? scene.background : FALLBACK_TEXTURE,
            ),
          }
        : null,
    )
    .filter((entry): entry is { texturePath: string } => entry !== null)

  if (chapterScenes.length === 0 || chapterSceneIds.length === 0) {
    return {
      destroy: () => {},
      update: () => {},
    }
  }

  let currentTexturePath: string | null = null

  const updateBackgroundTexture = (_forceRefresh: boolean = false) => {
    const chapterElements = Array.from(document.querySelectorAll('.chapter-title-scene')) as HTMLElement[]

    const vh = window.innerHeight
    const viewportCenterY = vh / 2

    // Map each visible chapter element to its story chapter index via data-scene (works with any subset of loaded scenes)
    let activeChapterIndex = 0
    let hasPassedAnyChapter = false
    for (const el of chapterElements) {
      if (!el?.isConnected) continue
      const sceneId = el.parentElement?.getAttribute('data-scene')
      if (sceneId == null) continue
      const storyChapterIndex = chapterSceneIds.indexOf(sceneId)
      if (storyChapterIndex === -1) continue
      const rect = el.getBoundingClientRect()
      if (rect.top < viewportCenterY) {
        hasPassedAnyChapter = true
        activeChapterIndex = Math.max(activeChapterIndex, storyChapterIndex)
      }
    }

    // Clear texture once the user reaches the epilogue area
    let inEpilogue = false
    const epilogueEls = document.querySelectorAll('.epilogue-scene-wrapper')
    for (const el of epilogueEls) {
      const rect = el.getBoundingClientRect()
      if (rect.top < viewportCenterY) { inEpilogue = true; break }
    }

    const entry = (hasPassedAnyChapter && !inEpilogue) ? (chapterScenes[activeChapterIndex] ?? chapterScenes[0]) : null
    const texturePath = entry ? entry.texturePath : null

    if (currentTexturePath !== texturePath) {
      currentTexturePath = texturePath
      updateTexture(texturePath)
    }
  }

  const updateTexture = (texturePath: string | null) => {
    document.body.style.backgroundImage = texturePath ? `url(${texturePath})` : 'none'
  }

  setTimeout(() => {
    updateBackgroundTexture()
  }, 100)

  frameCallbacks.push(updateBackgroundTexture)

  let bgRafId: number | null = null
  const scrollFallback = () => {
    if (bgRafId === null) {
      bgRafId = requestAnimationFrame(() => {
        updateBackgroundTexture()
        bgRafId = null
      })
    }
  }
  window.addEventListener('scroll', scrollFallback, { passive: true })

  return {
    destroy: () => {
      if (bgRafId !== null) cancelAnimationFrame(bgRafId)
      const index = frameCallbacks.indexOf(updateBackgroundTexture)
      if (index > -1) {
        frameCallbacks.splice(index, 1)
      }
      window.removeEventListener('scroll', scrollFallback)
    },
    update: () => updateBackgroundTexture(true),
  }
}
